/**
 * Générateur — Recueillir et organiser des données.
 * Attendu ID : 971 | Niveau : 5ème | Domaine : Statistiques (descripteur 131)
 *
 * Version conforme à la spécification 971 (2026-09-21). Les quatre cartes
 * d'origine (vocabulaire de l'enquête, tableau des sports, calibre des œufs,
 * deux classes de même moyenne) ont été supprimées : elles organisaient des
 * données déjà dénombrées (restriction du programme), ou relevaient des
 * objectifs voisins (fréquences 972, moyenne 976).
 *
 * Plan de cartes : chaque carte part d'un RELEVÉ BRUT (une observation par
 * individu, fabriqué d'abord) que l'élève dépouille dans un tableau
 * d'effectifs. Le tableau est un texte à trous multi-lignes « libellé : case »
 * (rendu en grille par l'app), une case par effectif, ligne et colonne de
 * totaux comprises.
 *   « Dépouiller un relevé » (niveau 1) — un caractère, 3 à 4 modalités,
 *     10 à 15 observations, toutes les modalités représentées.
 *   « Écrire le zéro » (niveau 2) — un caractère, 5 à 6 modalités, 20 à 30
 *     observations, au moins une modalité d'effectif nul.
 *   « Le tableau à double entrée » (niveau 3) — deux caractères croisés
 *     (classe × réponse), 3 à 4 modalités chacun, 30 à 40 observations,
 *     tableau complet avec totaux de lignes, de colonnes et total général.
 *   « Les réponses incomplètes » (niveau 4) — même tâche, 2 ou 3 observations
 *     dont la seconde réponse manque, comptées dans une colonne
 *     « Non renseigné » annoncée par la consigne.
 *
 * Spécification appliquée :
 *   Bornes : individus 10..40 (niveau 1 : 10..15, niveau 2 : 20..30,
 *     niveaux 3-4 : 30..40) ; modalités 3..6 par caractère (niveau 1 : 3..4,
 *     niveau 2 : 5..6, niveaux 3-4 : 3..4 chacun) ; caractères croisés 1..2 ;
 *     effectif d'une modalité 0..20 (0 impossible au niveau 1) ; questions de
 *     lecture après le tableau : 0.
 *   Exclusions : relevé jamais groupé par modalité (vérifié : les occurrences
 *     d'au moins une modalité ne sont pas contiguës, et l'ordre de première
 *     apparition diffère de l'ordre des colonnes) ; prénoms tous distincts ;
 *     chaque observation appartient à une modalité et une seule de la liste
 *     annoncée ; valeurs manquantes seulement au niveau 4, avec consigne.
 *   Erreurs visées → mots-clés (`_meta.params.distracteurs[].code`) :
 *     - recopier les nombres dans l'ordre du relevé → `ordre_du_releve`
 *     - confondre total de ligne et total de colonne → `ligne_pour_colonne`
 *     - laisser une case vide au lieu d'écrire zéro → `case_vide_pour_zero`
 *     - compter deux fois / oublier une observation sans recouper → `comptage_non_recoupe`
 *     - prendre le nombre de modalités pour effectif total → `modalites_pour_total`
 *   Graduation → cartes : niveau 1 « Dépouiller un relevé », niveau 2 « Écrire
 *     le zéro », niveau 3 « Le tableau à double entrée », niveau 4 « Les
 *     réponses incomplètes ».
 *   Non tranché : la grille de saisie de l'app ne distingue pas une case vide
 *     d'une case fausse (pas de message dédié) ; la colonne « Non renseigné »
 *     est annoncée par la consigne, l'élève ne la crée pas lui-même ; pas de
 *     variante feuille de calcul (aucune fabrique) ; le diagnostic global de
 *     cohérence des totaux n'existe pas dans le scoring.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 971;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DEPOUILLER = 'Dépouiller un relevé';
const L_ZERO = 'Écrire le zéro';
const L_DOUBLE = 'Le tableau à double entrée';
const L_INCOMPLET = 'Les réponses incomplètes';

// ─── Données de contexte ─────────────────────────────────────

const PRENOMS = [
    'Adam', 'Alice', 'Amine', 'Anaïs', 'Arthur', 'Aya', 'Bilal', 'Camille',
    'Chloé', 'Clara', 'Diego', 'Elias', 'Emma', 'Enzo', 'Ethan', 'Fatou',
    'Gabin', 'Hana', 'Hugo', 'Ibrahim', 'Inès', 'Jade', 'Jules', 'Kenza',
    'Léa', 'Lina', 'Louis', 'Lucas', 'Malo', 'Manon', 'Maya', 'Mila',
    'Nathan', 'Nina', 'Noé', 'Nora', 'Oscar', 'Rayan', 'Rose', 'Sacha',
    'Sami', 'Sofia', 'Théo', 'Tom', 'Yanis', 'Zoé', 'Ayoub', 'Lisa',
];

const CARACTERES = [
    { question: 'Quelle est ta couleur préférée ?', pool: ['Rouge', 'Bleu', 'Vert', 'Jaune', 'Violet', 'Orange'] },
    { question: 'Comment viens-tu au collège ?', pool: ['À pied', 'À vélo', 'En bus', 'En voiture', 'En trottinette', 'En train'] },
    { question: 'Quel animal de compagnie as-tu ?', pool: ['Chat', 'Chien', 'Lapin', 'Poisson', 'Hamster', 'Aucun'] },
    { question: 'Quel dessert as-tu choisi à la cantine ?', pool: ['Yaourt', 'Fruit', 'Compote', 'Gâteau', 'Glace', 'Fromage'] },
    { question: 'Quel sport pratiques-tu en club ?', pool: ['Football', 'Basket', 'Judo', 'Natation', 'Danse', 'Tennis'] },
    { question: 'Quelle activité choisis-tu pour la sortie ?', pool: ['Théâtre', 'Chorale', 'Échecs', 'Jardinage', 'Cinéma', 'Robotique'] },
];

const CLASSES = ['5eA', '5eB', '5eC', '5eD'];

// ─── Vérifications du relevé ─────────────────────────────────

/** Vrai si les occurrences de chaque valeur sont contiguës (relevé pré-trié). */
const estGroupe = (suite) => {
    let runs = 0;
    for (let i = 0; i < suite.length; i++) if (i === 0 || suite[i] !== suite[i - 1]) runs++;
    return runs === new Set(suite).size;
};

/** Ordre de première apparition des valeurs dans la suite. */
const ordreApparition = (suite) => {
    const vu = [];
    suite.forEach((v) => { if (!vu.includes(v)) vu.push(v); });
    return vu;
};

const memesListes = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/** Le relevé sert la tâche : pas groupé, et son ordre de collecte diffère de l'ordre des colonnes. */
const releveAcceptable = (suite, colonnes) => {
    if (estGroupe(suite)) return false;
    const app = ordreApparition(suite);
    return !memesListes(app, colonnes.filter((c) => app.includes(c)));
};

// ─── Tirage : un seul caractère (niveaux 1 et 2) ─────────────

/**
 * Fabrique le relevé brut d'abord (une réponse par individu), puis en déduit
 * les effectifs. `absentes` = nombre de modalités de la liste annoncée que
 * personne n'a choisies (niveau 2).
 */
const tirerSimple = ({ kMin, kMax, nMin, nMax, absentes }) => {
    for (let essai = 0; essai < 80; essai++) {
        const car = randomChoice(CARACTERES);
        const k = randInt(kMin, kMax);
        const n = randInt(nMin, nMax);
        const modalites = shuffle(car.pool).slice(0, k);
        const nbAbs = absentes ? Math.min(absentes, k - 3) : 0;
        const presentes = modalites.slice(nbAbs);   // les absentes seront mélangées dans l'ordre des colonnes
        const colonnes = shuffle(modalites);
        const suite = Array.from({ length: n }, () => randomChoice(presentes));
        const effectifs = colonnes.map((m) => suite.filter((v) => v === m).length);
        if (presentes.some((m) => !suite.includes(m))) continue;
        if (effectifs.some((e) => e > 20)) continue;
        if (!releveAcceptable(suite, colonnes)) continue;
        const prenoms = shuffle(PRENOMS).slice(0, n);
        return { car, k, n, colonnes, effectifs, suite, prenoms, nbAbs, alpha: randInt(0, 1) === 0 };
    }
    throw new Error('gen_971 : relevé simple introuvable');
};

/** Le relevé brut affiché : suite de réponses (ordre de collecte) ou liste alphabétique. */
const releveSimpleHtml = (d) => {
    if (!d.alpha) {
        return `<p>Voici les ${d.n} réponses, dans l'ordre où elles ont été recueillies :</p>`
            + `<p>${d.suite.join(', ')}.</p>`;
    }
    const lignes = d.prenoms.map((p, i) => [p, d.suite[i]])
        .sort((a, b) => a[0].localeCompare(b[0], 'fr'));
    return `<p>Voici les ${d.n} réponses, élève par élève, dans l'ordre alphabétique :</p>`
        + `<p>${lignes.map(([p, v]) => `${p} : ${v}`).join(' ; ')}.</p>`;
};

const introSimple = (d) =>
    `<p>Dans une classe, on a posé à chaque élève la question : « ${d.car.question} » `
    + `Les réponses possibles étaient : ${d.colonnes.join(', ')}.</p>${releveSimpleHtml(d)}`;

const paramsSimple = (d, niveau, extra = {}) => ({
    niveau,
    nombre_d_individus_de_la_serie: d.n,
    nombre_de_modalites_du_caractere: d.k,
    nombre_de_caracteres_croises: 1,
    effectif_d_une_modalite: d.effectifs,
    effectif_total_de_la_serie: d.effectifs.reduce((a, b) => a + b, 0),
    longueur_du_releve_brut: d.suite.length,
    nombre_de_questions_de_lecture_apres_le_tableau: 0,
    releve_groupe: estGroupe(d.suite),
    ordre_releve_egal_colonnes: memesListes(ordreApparition(d.suite), d.colonnes.filter((c) => d.suite.includes(c))),
    prenoms_distincts: new Set(d.prenoms).size === d.prenoms.length,
    modalites_absentes: d.effectifs.filter((e) => e === 0).length,
    ...extra,
});

/** Effectif que l'élève écrirait dans la colonne j en recopiant dans l'ordre du relevé. */
const effectifOrdreReleve = (d, j) => {
    const app = ordreApparition(d.suite);
    const m = app[j];
    return m === undefined ? 0 : d.suite.filter((v) => v === m).length;
};

// ─── Carte « Dépouiller un relevé » (niveau 1) ───────────────

const PARAMS_N1 = { kMin: 3, kMax: 4, nMin: 10, nMax: 15, absentes: 0 };

const genDepouillerTrous = () => {
    const d = tirerSimple(PARAMS_N1);
    const phrase = [...d.colonnes.map((m, j) => `${m} : {{${d.effectifs[j]}}}`), `Total : {{${d.n}}}`].join('\n');
    const payload = makeTexteTrou(phrase,
        `${introSimple(d)}<p>Complète le tableau des effectifs : une case par réponse possible, puis le total.</p>`,
        { ...opts(L_DEPOUILLER), mode: 'saisie', strict: false });
    payload._meta.params = paramsSimple(d, 1, { erreurVisee: 'ordre_du_releve' });
    return payload;
};

const genDepouillerQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerSimple(PARAMS_N1);
        if (randInt(0, 1) === 0) {
            // Effectif d'une modalité
            const j = randInt(0, d.k - 1);
            const bonne = d.effectifs[j];
            const dist = [
                { code: 'ordre_du_releve', valeur: effectifOrdreReleve(d, j) },
                { code: 'comptage_non_recoupe', valeur: bonne + (randInt(0, 1) === 0 ? 1 : -1) },
                { code: 'modalites_pour_total', valeur: d.k },
            ];
            const valeurs = [bonne, ...dist.map((x) => x.valeur)];
            if (new Set(valeurs).size !== 4 || valeurs.some((v) => v < 0)) continue;
            const payload = makeQcm(valeurs.map(String), String(bonne),
                `${introSimple(d)}<p>Quel est l'effectif de la réponse « ${d.colonnes[j]} » ?</p>`,
                opts(L_DEPOUILLER));
            payload._meta.params = paramsSimple(d, 1, { distracteurs: dist });
            return payload;
        }
        // Effectif total
        const bonne = d.n;
        const dist = [
            { code: 'modalites_pour_total', valeur: d.k },
            { code: 'comptage_non_recoupe', valeur: bonne + 1 },
            { code: 'comptage_non_recoupe', valeur: bonne - 1 },
        ];
        const valeurs = [bonne, ...dist.map((x) => x.valeur)];
        if (new Set(valeurs).size !== 4) continue;
        const payload = makeQcm(valeurs.map(String), String(bonne),
            `${introSimple(d)}<p>Quel est l'effectif total de cette série ?</p>`,
            opts(L_DEPOUILLER));
        payload._meta.params = paramsSimple(d, 1, { distracteurs: dist });
        return payload;
    }
    throw new Error('gen_971 : QCM niveau 1 introuvable');
};

const genDepouillerVF = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerSimple(PARAMS_N1);
        const [j1, j2] = shuffle(d.colonnes.map((_, i) => i)).slice(0, 2);
        const codes = [];
        const lignes = [];
        // Ligne 1 : effectif d'une modalité, vrai ou « ordre du relevé »
        const faux1 = effectifOrdreReleve(d, j1);
        const vrai1 = randInt(0, 1) === 0 || faux1 === d.effectifs[j1];
        lignes.push([`L'effectif de la réponse « ${d.colonnes[j1]} » est ${vrai1 ? d.effectifs[j1] : faux1}.`, vrai1 ? 0 : 1]);
        if (!vrai1) codes.push('ordre_du_releve');
        // Ligne 2 : effectif d'une autre modalité, vrai ou décalé d'une unité
        const vrai2 = randInt(0, 1) === 0;
        const delta = randInt(0, 1) === 0 ? 1 : -1;
        lignes.push([`L'effectif de la réponse « ${d.colonnes[j2]} » est ${vrai2 ? d.effectifs[j2] : d.effectifs[j2] + delta}.`, vrai2 ? 0 : 1]);
        if (!vrai2) codes.push('comptage_non_recoupe');
        // Ligne 3 : total, vrai ou nombre de modalités
        const vrai3 = randInt(0, 1) === 0;
        lignes.push([`L'effectif total de la série est ${vrai3 ? d.n : d.k}.`, vrai3 ? 0 : 1]);
        if (!vrai3) codes.push('modalites_pour_total');
        // Ligne 4 : recoupement de la somme des effectifs avec le nombre d'observations
        const vrai4 = randInt(0, 1) === 0;
        lignes.push([`La somme des effectifs du tableau doit être ${vrai4 ? d.n : d.n + (randInt(0, 1) === 0 ? 1 : -1)}.`, vrai4 ? 0 : 1]);
        if (!vrai4) codes.push('comptage_non_recoupe');
        if (d.effectifs[j2] + delta < 0) continue;
        const nbVrai = lignes.filter((l) => l[1] === 0).length;
        if (nbVrai === 0 || nbVrai === lignes.length) continue;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(melange.map((l) => l[0]), melange.map((l) => l[1]),
            `${introSimple(d)}<p>Après avoir dépouillé le relevé, réponds : vrai ou faux ?</p>`, opts(L_DEPOUILLER));
        payload._meta.params = paramsSimple(d, 1, { distracteurs: codes.map((code) => ({ code })) });
        return payload;
    }
    throw new Error('gen_971 : V/F niveau 1 introuvable');
};

// ─── Carte « Écrire le zéro » (niveau 2) ─────────────────────

const PARAMS_N2 = { kMin: 5, kMax: 6, nMin: 20, nMax: 30, absentes: 1 };

const tirerZero = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerSimple({ ...PARAMS_N2, absentes: randInt(1, 2) });
        if (d.effectifs.some((e) => e === 0)) return d;
    }
    throw new Error('gen_971 : relevé avec modalité absente introuvable');
};

const consigneZero = '<p>Complète toutes les cases du tableau des effectifs, puis le total.</p>';

const genZeroTrous = () => {
    const d = tirerZero();
    const phrase = [...d.colonnes.map((m, j) => `${m} : {{${d.effectifs[j]}}}`), `Total : {{${d.n}}}`].join('\n');
    const payload = makeTexteTrou(phrase, `${introSimple(d)}${consigneZero}`,
        { ...opts(L_ZERO), mode: 'saisie', strict: false });
    payload._meta.params = paramsSimple(d, 2, { erreurVisee: 'case_vide_pour_zero' });
    return payload;
};

const genZeroQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerZero();
        const jZero = d.effectifs.indexOf(0);
        if (randInt(0, 1) === 0) {
            const bonne = '0';
            const dist = [
                { code: 'case_vide_pour_zero', valeur: 'Rien : la case reste vide' },
                { code: 'modalites_pour_total', valeur: String(d.k) },
                { code: 'comptage_non_recoupe', valeur: '1' },
            ];
            const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
                `${introSimple(d)}<p>Que doit-on écrire dans la case de la réponse « ${d.colonnes[jZero]} » du tableau des effectifs ?</p>`,
                opts(L_ZERO));
            payload._meta.params = paramsSimple(d, 2, { distracteurs: dist });
            return payload;
        }
        const bonne = d.n;
        const dist = [
            { code: 'modalites_pour_total', valeur: d.k },
            { code: 'modalites_pour_total', valeur: d.k - d.effectifs.filter((e) => e === 0).length },
            { code: 'comptage_non_recoupe', valeur: bonne + (randInt(0, 1) === 0 ? 1 : -1) },
        ];
        const valeurs = [bonne, ...dist.map((x) => x.valeur)];
        if (new Set(valeurs).size !== 4) continue;
        const payload = makeQcm(valeurs.map(String), String(bonne),
            `${introSimple(d)}<p>Quel est l'effectif total de cette série ?</p>`,
            opts(L_ZERO));
        payload._meta.params = paramsSimple(d, 2, { distracteurs: dist });
        return payload;
    }
    throw new Error('gen_971 : QCM niveau 2 introuvable');
};

const genZeroVF = () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerZero();
        const jZero = d.effectifs.indexOf(0);
        const jPres = randomChoice(d.colonnes.map((_, i) => i).filter((i) => d.effectifs[i] > 0));
        const codes = [];
        const lignes = [];
        const vrai1 = randInt(0, 1) === 0;
        lignes.push([vrai1
            ? `Dans le tableau, la case de la réponse « ${d.colonnes[jZero]} » contient 0.`
            : `Dans le tableau, la case de la réponse « ${d.colonnes[jZero]} » reste vide.`, vrai1 ? 0 : 1]);
        if (!vrai1) codes.push('case_vide_pour_zero');
        const vrai2 = randInt(0, 1) === 0;
        const faux2 = effectifOrdreReleve(d, jPres);
        const v2 = vrai2 || faux2 === d.effectifs[jPres];
        lignes.push([`L'effectif de la réponse « ${d.colonnes[jPres]} » est ${v2 ? d.effectifs[jPres] : faux2}.`, v2 ? 0 : 1]);
        if (!v2) codes.push('ordre_du_releve');
        const vrai3 = randInt(0, 1) === 0;
        lignes.push([`L'effectif total de la série est ${vrai3 ? d.n : d.k}.`, vrai3 ? 0 : 1]);
        if (!vrai3) codes.push('modalites_pour_total');
        const vrai4 = randInt(0, 1) === 0;
        lignes.push([`La somme des effectifs du tableau doit être ${vrai4 ? d.n : d.n + (randInt(0, 1) === 0 ? 1 : -1)}.`, vrai4 ? 0 : 1]);
        if (!vrai4) codes.push('comptage_non_recoupe');
        const nbVrai = lignes.filter((l) => l[1] === 0).length;
        if (nbVrai === 0 || nbVrai === lignes.length) continue;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(melange.map((l) => l[0]), melange.map((l) => l[1]),
            `${introSimple(d)}<p>Après avoir dépouillé le relevé, réponds : vrai ou faux ?</p>`, opts(L_ZERO));
        payload._meta.params = paramsSimple(d, 2, { distracteurs: codes.map((code) => ({ code })) });
        return payload;
    }
    throw new Error('gen_971 : V/F niveau 2 introuvable');
};

// ─── Tirage : deux caractères croisés (niveaux 3 et 4) ───────

/**
 * Relevé brut à deux caractères : la classe (lignes) et la réponse à une
 * question (colonnes). `incompletes` observations n'ont pas de seconde réponse
 * (niveau 4) et vont dans la colonne « Non renseigné ».
 */
const tirerCroise = ({ incompletes }) => {
    for (let essai = 0; essai < 120; essai++) {
        const car = randomChoice(CARACTERES);
        const kL = randInt(3, 4);
        const kC = randInt(3, 4);
        const n = randInt(30, 40);
        const lignes = CLASSES.slice(0, kL);
        const colonnes = shuffle(car.pool).slice(0, kC);
        const nbInc = incompletes ? randInt(2, 3) : 0;
        const obs = Array.from({ length: n }, (_, i) => ({
            classe: randomChoice(lignes),
            reponse: i < nbInc ? null : randomChoice(colonnes),
        }));
        const melange = shuffle(obs);
        const suiteL = melange.map((o) => o.classe);
        const suiteC = melange.filter((o) => o.reponse !== null).map((o) => o.reponse);
        const cellule = (l, c) => melange.filter((o) => o.classe === l && o.reponse === c).length;
        const table = lignes.map((l) => colonnes.map((c) => cellule(l, c)));
        const nonRens = lignes.map((l) => melange.filter((o) => o.classe === l && o.reponse === null).length);
        const totL = lignes.map((l, i) => table[i].reduce((a, b) => a + b, 0) + nonRens[i]);
        const totC = colonnes.map((c, j) => table.reduce((a, r) => a + r[j], 0));
        if (totL.some((t) => t < 1 || t > 20) || totC.some((t) => t < 1 || t > 20)) continue;
        if (!releveAcceptable(suiteL, lignes) || !releveAcceptable(suiteC, colonnes)) continue;
        const prenoms = shuffle(PRENOMS).slice(0, n);
        const alpha = randInt(0, 1) === 0;
        const indiv = melange.map((o, i) => ({ ...o, prenom: prenoms[i] }));
        if (alpha) indiv.sort((a, b) => a.prenom.localeCompare(b.prenom, 'fr'));
        return { car, kL, kC, n, lignes, colonnes, table, nonRens, totL, totC, nbInc, indiv, alpha, suiteL, suiteC, prenoms };
    }
    throw new Error('gen_971 : relevé croisé introuvable');
};

const introCroise = (d) => {
    const ordre = d.alpha ? 'dans l\'ordre alphabétique' : 'dans l\'ordre où les réponses ont été recueillies';
    const inc = d.nbInc > 0
        ? ` ${d.nbInc} élèves n'ont pas répondu à la question : leur ligne porte la mention « pas de réponse ».`
        : '';
    return `<p>Dans les classes de 5e d'un collège, on a posé à chaque élève la question : « ${d.car.question} » `
        + `Les réponses possibles étaient : ${d.colonnes.join(', ')}.${inc}</p>`
        + `<p>Voici le relevé des ${d.n} élèves interrogés, ${ordre} :</p>`
        + `<p>${d.indiv.map((o) => `${o.prenom} (${o.classe}) : ${o.reponse === null ? 'pas de réponse' : o.reponse}`).join(' ; ')}.</p>`;
};

const paramsCroise = (d, niveau, extra = {}) => ({
    niveau,
    nombre_d_individus_de_la_serie: d.n,
    nombre_de_modalites_du_caractere: [d.kL, d.kC],
    nombre_de_caracteres_croises: 2,
    effectif_d_une_modalite: [...d.totL, ...d.totC],
    effectif_total_de_la_serie: d.totL.reduce((a, b) => a + b, 0),
    longueur_du_releve_brut: d.indiv.length,
    nombre_de_questions_de_lecture_apres_le_tableau: 0,
    releve_groupe: estGroupe(d.suiteL) || estGroupe(d.suiteC),
    ordre_releve_egal_colonnes: memesListes(ordreApparition(d.suiteC), d.colonnes.filter((c) => d.suiteC.includes(c))),
    prenoms_distincts: new Set(d.prenoms).size === d.prenoms.length,
    observations_incompletes: d.nbInc,
    ...extra,
});

/** Grille à trous : une ligne par classe, une ligne de totaux. */
const phraseCroise = (d) => {
    const entetes = [...d.colonnes, ...(d.nbInc > 0 ? ['Non renseigné'] : [])];
    const ligne = (i) => {
        const cases = [...d.table[i], ...(d.nbInc > 0 ? [d.nonRens[i]] : [])];
        return `${d.lignes[i]} : ${entetes.map((c, j) => `${c} {{${cases[j]}}}`).join(', ')}, total {{${d.totL[i]}}}`;
    };
    const totaux = [...d.totC, ...(d.nbInc > 0 ? [d.nonRens.reduce((a, b) => a + b, 0)] : [])];
    return [
        ...d.lignes.map((_, i) => ligne(i)),
        `Total : ${entetes.map((c, j) => `${c} {{${totaux[j]}}}`).join(', ')}, total général {{${d.n}}}`,
    ].join('\n');
};

const consigneCroise = (d) => (d.nbInc > 0
    ? '<p>Complète le tableau à double entrée : une case par classe et par réponse, une colonne « Non renseigné » pour les élèves qui n\'ont pas répondu, '
        + 'le total de chaque ligne, le total de chaque colonne et le total général. Vérifie que le total général se retrouve dans les deux sens.</p>'
    : '<p>Complète le tableau à double entrée : une case par classe et par réponse, le total de chaque ligne, le total de chaque colonne '
        + 'et le total général. Vérifie que le total général se retrouve dans les deux sens.</p>');

const genCroiseTrous = (incompletes, label) => () => {
    const d = tirerCroise({ incompletes });
    const payload = makeTexteTrou(phraseCroise(d), `${introCroise(d)}${consigneCroise(d)}`,
        { ...opts(label), mode: 'saisie', strict: false });
    payload._meta.params = paramsCroise(d, incompletes ? 4 : 3, { erreurVisee: incompletes ? 'comptage_non_recoupe' : 'ligne_pour_colonne' });
    return payload;
};

const genCroiseQcm = (incompletes, label) => () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerCroise({ incompletes });
        const variante = incompletes ? randInt(0, 2) : randInt(0, 1);
        const i = randInt(0, d.kL - 1);
        const j = randInt(0, d.kC - 1);
        let bonne;
        let dist;
        let question;
        if (variante === 0) {
            // Total d'une ligne (élèves d'une classe) contre total d'une colonne
            bonne = d.totL[i];
            dist = [
                { code: 'ligne_pour_colonne', valeur: d.totC[j] },
                { code: 'comptage_non_recoupe', valeur: bonne + (randInt(0, 1) === 0 ? 1 : -1) },
                { code: 'modalites_pour_total', valeur: d.kC },
            ];
            question = `<p>Combien d'élèves de ${d.lignes[i]} figurent dans le relevé ?</p>`;
        } else if (variante === 1) {
            // Total d'une colonne (une réponse) contre total d'une ligne
            bonne = d.totC[j];
            dist = [
                { code: 'ligne_pour_colonne', valeur: d.totL[i] },
                { code: 'comptage_non_recoupe', valeur: bonne + (randInt(0, 1) === 0 ? 1 : -1) },
                { code: 'ordre_du_releve', valeur: d.table[i][j] },
            ];
            question = `<p>Combien d'élèves, toutes classes confondues, ont répondu « ${d.colonnes[j]} » ?</p>`;
        } else {
            // Niveau 4 : total général, avec ou sans les réponses incomplètes
            bonne = d.n;
            dist = [
                { code: 'comptage_non_recoupe', valeur: d.n - d.nbInc },
                { code: 'comptage_non_recoupe', valeur: d.n + d.nbInc },
                { code: 'modalites_pour_total', valeur: d.kL * (d.kC + 1) },
            ];
            question = '<p>Quel est le total général du tableau, une fois les élèves sans réponse comptés dans la colonne « Non renseigné » ?</p>';
        }
        const valeurs = [bonne, ...dist.map((x) => x.valeur)];
        if (new Set(valeurs).size !== 4) continue;
        const payload = makeQcm(valeurs.map(String), String(bonne), `${introCroise(d)}${question}`, opts(label));
        payload._meta.params = paramsCroise(d, incompletes ? 4 : 3, { distracteurs: dist });
        return payload;
    }
    throw new Error('gen_971 : QCM croisé introuvable');
};

const genCroiseVF = (incompletes, label) => () => {
    for (let essai = 0; essai < 60; essai++) {
        const d = tirerCroise({ incompletes });
        const i = randInt(0, d.kL - 1);
        const j = randInt(0, d.kC - 1);
        const codes = [];
        const lignes = [];
        // Ligne 1 : total général
        const vrai1 = randInt(0, 1) === 0;
        const faux1 = incompletes ? d.n - d.nbInc : d.n + (randInt(0, 1) === 0 ? 1 : -1);
        lignes.push([`Le total général du tableau est ${vrai1 ? d.n : faux1}.`, vrai1 ? 0 : 1]);
        if (!vrai1) codes.push('comptage_non_recoupe');
        // Ligne 2 : total d'une ligne contre total d'une colonne
        const vrai2 = randInt(0, 1) === 0 || d.totL[i] === d.totC[j];
        lignes.push([`Le relevé compte ${vrai2 ? d.totL[i] : d.totC[j]} élèves de ${d.lignes[i]}.`, vrai2 ? 0 : 1]);
        if (!vrai2) codes.push('ligne_pour_colonne');
        // Ligne 3 : une case du tableau
        const vrai3 = randInt(0, 1) === 0;
        const faux3 = d.table[i][j] + 1;
        lignes.push([`${vrai3 ? d.table[i][j] : faux3} élèves de ${d.lignes[i]} ont répondu « ${d.colonnes[j]} ».`, vrai3 ? 0 : 1]);
        if (!vrai3) codes.push('comptage_non_recoupe');
        // Ligne 4 : recoupement des totaux, ou colonne Non renseigné
        const vrai4 = randInt(0, 1) === 0;
        if (incompletes) {
            lignes.push([vrai4
                ? `La colonne « Non renseigné » a pour total ${d.nbInc}.`
                : 'Les élèves qui n\'ont pas répondu ne sont pas comptés dans le total général.', vrai4 ? 0 : 1]);
            if (!vrai4) codes.push('comptage_non_recoupe');
        } else {
            lignes.push([vrai4
                ? 'La somme des totaux de lignes et la somme des totaux de colonnes donnent le même nombre.'
                : `La somme des totaux de colonnes est ${d.n + (randInt(0, 1) === 0 ? 1 : -1)}.`, vrai4 ? 0 : 1]);
            if (!vrai4) codes.push('comptage_non_recoupe');
        }
        const nbVrai = lignes.filter((l) => l[1] === 0).length;
        if (nbVrai === 0 || nbVrai === lignes.length) continue;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(melange.map((l) => l[0]), melange.map((l) => l[1]),
            `${introCroise(d)}<p>Après avoir dépouillé le relevé dans un tableau à double entrée, réponds : vrai ou faux ?</p>`, opts(label));
        payload._meta.params = paramsCroise(d, incompletes ? 4 : 3, { distracteurs: codes.map((code) => ({ code })) });
        return payload;
    }
    throw new Error('gen_971 : V/F croisé introuvable');
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    depouiller_texte_trou_saisie: genDepouillerTrous,
    depouiller_qcm_unique: genDepouillerQcm,
    depouiller_tableau_vf: genDepouillerVF,
    zero_texte_trou_saisie: genZeroTrous,
    zero_qcm_unique: genZeroQcm,
    zero_tableau_vf: genZeroVF,
    double_texte_trou_saisie: genCroiseTrous(false, L_DOUBLE),
    double_qcm_unique: genCroiseQcm(false, L_DOUBLE),
    double_tableau_vf: genCroiseVF(false, L_DOUBLE),
    incomplet_texte_trou_saisie: genCroiseTrous(true, L_INCOMPLET),
    incomplet_qcm_unique: genCroiseQcm(true, L_INCOMPLET),
    incomplet_tableau_vf: genCroiseVF(true, L_INCOMPLET),
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'depouiller', label: L_DEPOUILLER, formatsList: buildFormats('depouiller') },
    { id: 'zero', label: L_ZERO, formatsList: buildFormats('zero') },
    { id: 'double', label: L_DOUBLE, formatsList: buildFormats('double') },
    { id: 'incomplet', label: L_INCOMPLET, formatsList: buildFormats('incomplet') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
